import type { Component, PropType, SetupContext, VNodeChild } from 'vue';
import { defineComponent, h } from 'vue';

/** Callable component shape used for direct render-function invocation. */
type CallableRenderComponent = (props: Record<string, unknown>, context: SetupContext) => VNodeChild;

/**
 * Check whether a function carries Vue component options that Vue must process.
 */
function isConfiguredFunctionalComponent(component: Component): boolean {
	return typeof component === 'function'
		&& ('props' in component || 'emits' in component || 'inheritAttrs' in component || 'compatConfig' in component);
}

/**
 * Render dynamic component functions through stable component identity.
 * Changing `renderFn` updates rendered VNodes without remounting this wrapper.
 */
export const RenderFnWrapper = defineComponent({
	name: 'RenderFnWrapper',
	inheritAttrs: false,
	props: {
		/** Dynamic component or render function invoked by this stable wrapper. */
		renderFn: {
			type: [Function, Object] as PropType<Component>,
			required: true,
		},
	},
	/** Render current function with attributes forwarded by wrapper consumers. */
	setup(props, context) {
		return () => {
			if (typeof props.renderFn === 'function' && !isConfiguredFunctionalComponent(props.renderFn)) {
				// Invoke render functions inside this stable component boundary. Passing
				// them to h() would make their changing reference the child VNode identity.
				return (props.renderFn as CallableRenderComponent)(context.attrs, context);
			}

			// Configured functional and object components need Vue's prop, emit, and
			// attribute handling, so preserve their normal component semantics.
			return h(props.renderFn, context.attrs);
		};
	},
});
